import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import _ from 'lodash';
import { Button, Divider, Typography } from '@material-ui/core';

import { CommonDialog, AutoComplete, FormField2 as Field } from 'components';

import { history } from 'utils/history';
import { toast } from 'utils/helpers';
import api from 'utils/api';
import * as objectService from 'services/estimateObjects';
import * as projectService from 'services/projects';
import * as recoveryPriceService from 'services/recoveryPrice';

const EstimateObjectImportModal = (props) => {
  const { t } = useTranslation();
  const { onClose, onSelect, versionCode, versionYear, isEstimateObject = true, order, summaryId, parent, projectId } = props;

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [matchVersion, setMatchVersion] = useState(false);
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    getProjects();
  }, []);

  const handleClose = () => {
    !!onClose && onClose();
  }

  const getProjects = async () => {
    const projects = await projectService.list({ lite: true });
    setProjects(projects);

    if (props.projectId) {
      handleChange({ name: 'project', value: props.projectId })
    }
  }

  const getObjects = async (projectId) => {
    if (!projectId) return;

    const [estimateObjects, recoveryPrices] = await Promise.all([
      objectService.list({ projectId }),
      recoveryPriceService.list({ projectId })
    ]);

    const mappedEstimateObjects = estimateObjects.map(obj => ({ ...obj, _type: 'estimate' }));
    const mappedRecoveryPrices = recoveryPrices.map(obj => ({ ...obj, _type: 'recovery' }));
    
    const allObjects = [...mappedEstimateObjects, ...mappedRecoveryPrices];
    const sortedObjects = allObjects.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

    setObjects(sortedObjects);
  }

  const handleChange = ({ name, value }) => {
    if (name == 'project') {
      setSelectedObject(null);
      setSelectedProject(value);
      getObjects(value);
    }

    if (name == 'object') {
      setSelectedObject(value);
    }

    if (name == 'type') {
      setType(value);
    }

    if (name == 'title') {
      setTitle(value);
    }
  };

  const handleSelect = () => {
    if (!selectedObject) return;

    const shouldMatchVersion = !objectVersionMatches() && matchVersion;

    const selectedObj = objects.find(o => o._id === selectedObject);
    const isEstObj = selectedObj?._type === 'estimate';

    onSelect(selectedObject, shouldMatchVersion, isEstObj, order);
  }

  const handleCreate = async () => {
    try {
      const { data } = await api.post(`/estimate-summaries/${summaryId}/create-object`, {
        type,
        title,
        parent: parent || null,
        order: order || null,
      });

      // depending on type redirect to /objects/id or /recovery-prices/id
      if (type == 'estimate') {
        window.location.href = (`/projects/${props.projectId}/objects/${data.estimateObject}`);
      }
      if (type == 'ntk') {
        window.location.href = (`/recovery-price/${data.recoveryPrice}`);
      }
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko sukurti skaičiavimo'), 'error');
    }
  }

  const objectVersionMatches = () => {
    const selectedObj = objects.find(object => object._id === selectedObject);
    if (!selectedObj) return false;

    if (selectedObj._type === 'estimate') {
      return selectedObj?.version?.code === versionCode;
    } else if (selectedObj._type === 'recovery') {
      // For recovery price objects
      return selectedObj?.pricing?.version === versionYear;
    }
  }

  return (
    <>
      <CommonDialog
        visible
        onClose={handleClose}
        maxWidth="sm"
        // style={{ position: 'relative' }}
        // title={t('Skaičiavimo objekto paieška')}
        actions={[
          <Button
            color="default"
            variant="text"
            onClick={() => handleClose()}
          >
            {t("Atšaukti")}
          </Button>,
          <Button
            color="primary"
            variant="contained"
            disabled={!selectedObject || (!objectVersionMatches() && !matchVersion)}
            onClick={() => handleSelect()}
          >
            {t('Įkelti skaičiavimą')}
          </Button>,
          <Button
            color="primary"
            variant="contained"
            disabled={!title.length || !type}
            onClick={() => handleCreate()}
          >
            {t('Sukurti skaičiavimą')}
          </Button>
        ]}
      >
        <div>
          <Typography variant="h5" style={{ marginBottom: '10px' }}>
            {t('Įkelti skaičiavimą')}
          </Typography>
          <AutoComplete
            name="project"
            label={t('Projektas')}
            options={projects.map((project) => ({
              key: project._id,
              value: project._id,
              title: project.code ? `${project.code} - ${project.title}` : project.title,
            }))}
            freeSolo={true}
            creatable={false}
            value={selectedProject}
            style={{ height: '32px', marginTop: '8px', marginBottom: '20px' }}
            onChange={(newValue) => handleChange({ name: 'project', value: newValue?.value })}
          />

          <AutoComplete
            name="object"
            label={t('Pasirinkti iš sąrašo')}
            options={objects.map((object) => {
              let title = object.title;
              if (object._type === 'estimate') {
                title = versionCode !== object?.version?.code
                  ? `${object.title} (${t('kainynas')} ${object?.version?.version})`
                  : object.title;
                title += ` (${t('Sąmata')})`;
              }

              if (object._type === 'recovery') {
                title = versionCode !== object?.pricing?.code
                  ? `${object.title} (${t('kainynas')} ${object?.pricing?.version})`
                  : object.title;
                title += ' (NTK)';
              }
              return {
                ...object,
                title
              };
            })}
            freeSolo={true}
            creatable={false}
            disabled={!selectedProject}
            value={selectedObject}
            style={{ height: '32px', marginTop: '8px', marginBottom: '10px' }}
            onChange={(newValue) => handleChange({ name: 'object', value: newValue?._id })}
          />

          {!objectVersionMatches() && !!selectedObject && (
            <Field
              label={t('Perskaičiuoti suvestinio skaičiavimo versijoje')}
              type="checkbox"
              name="matchVersion"
              onChange={(({ value }) => setMatchVersion(value))}
              value={matchVersion}
            />
          )}
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <Typography variant="h5" style={{ marginTop: '10px' }}>
          {t('Naujas skaičiavimas')}
        </Typography>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <Field
            type="text"
            name="title"
            label={t('Pavadinimas')}
            value={title}
            wrapperStyle={{ flex: 1, minWidth: 0 }}
            style={{ height: '32px' }}
            onChange={(newValue) => handleChange({ name: 'title', value: newValue?.value })}
          />

          <Field
            type="select"
            name="type"
            label={t('Skaičiavimo tipas')}
            options={[
              { key: 'ntk', value: 'ntk', label: t('Atkuriamosios vertės skaičiavimai') },
              { key: 'estimate', value: 'estimate', label: t('Sąmatų skaičiavimai') },
            ]}
            value={type}
            wrapperStyle={{ flex: 1, minWidth: 0, paddingTop: '8px' }}
            style={{ height: '32px' }}
            onChange={(newValue) => handleChange({ name: 'type', value: newValue?.value })}
          />
        </div>
      </CommonDialog>
    </>
  )
}

export default EstimateObjectImportModal;